import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useAudienceStore } from '@/stores/audience-store';
import { isWeeklyFeedOnlyOrder } from '@/lib/products';
import { CONTENT_STEP_PATH, WIZARD_STEPS, matchesStepPath, type WizardStep } from './steps';

/**
 * The steps this wizard session actually walks (NPJB-517).
 *
 * Content is dropped when the whole order is a 3rd Eye weekly data feed, which
 * makes Products → Continue/Skip land on Review and Review → Back return to
 * Products. Everything falls out of the list itself: `WizardTabs` renders it and
 * `WizardBottomBar` derives every navigation decision from it, so adding a
 * content-requiring product later reinstates the step with no extra wiring.
 *
 * `SelectedProduct.id` holds the catalog slug (NPJB-298), which is what the
 * predicate matches on.
 *
 * The one concession is a user *standing on* Content — a refresh or deep link on
 * a weekly-feed-only draft. Filtering the step out from under them would leave
 * `stepIndexFromPathname` at `-1`, stranding them with no Back and a dead
 * Continue, so the current step is always kept. It disappears from the flow the
 * moment they leave it.
 */
export const useWizardSteps = (): readonly WizardStep[] => {
  const { pathname } = useLocation();
  const selectedProducts = useAudienceStore((s) => s.products.selectedProducts);
  const productAnswers = useAudienceStore((s) => s.products.productAnswers);

  return useMemo(() => {
    if (matchesStepPath(pathname, CONTENT_STEP_PATH)) return WIZARD_STEPS;

    const skipContent = isWeeklyFeedOnlyOrder({
      productSelections: selectedProducts.map((p) => ({ slug: p.id, name: p.name })),
      productAnswers,
    });
    if (!skipContent) return WIZARD_STEPS;

    return WIZARD_STEPS.filter((step) => step.path !== CONTENT_STEP_PATH);
  }, [pathname, selectedProducts, productAnswers]);
};
